import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { sanitizeText, sanitizeEmail, sanitizeNumber, sanitizePhone, sanitizeAlphanumeric } from '../utils/sanitize';

interface PersonalInfo {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  nationalId: string;
  physicalAddress: string;
  city: string;
  province: string;
  phoneNumber: string;
  email: string;
  employmentStatus: string;
  employerName: string;
  employerAddress: string;
  jobTitle: string;
  businessName: string;
  businessType: string;
  businessRegistration: string;
  monthlyIncome: string;
}

interface LoanDetails {
  loanType?: string;
  loanAmount?: number;
  loanTerm?: number;
  loanPurpose?: string;
  otherPurpose?: string;
  businessRegNumber?: string;
  yearsInOperation?: string;
  assetDescription?: string;
  assetValue?: string;
}

interface UploadedFile {
  id: string;
  file: File;
  preview?: string;
  status: 'uploading' | 'uploaded' | 'error';
  progress: number;
}

interface ApplicationData {
  personalInfo: PersonalInfo;
  loanDetails: LoanDetails;
  documents?: Record<string, UploadedFile>;
}

interface LoanApplicationContextType {
  applicationData: ApplicationData;
  currentStep: number;
  updatePersonalInfo: (info: Partial<PersonalInfo>) => void;
  updateLoanDetails: (details: Partial<LoanDetails>) => void;
  updateDocuments: (documents: Record<string, UploadedFile>) => void;
  setCurrentStep: (step: number) => void;
  resetApplication: () => void;
}

const defaultPersonalInfo: PersonalInfo = {
  firstName: '',
  lastName: '',
  dateOfBirth: '',
  gender: '',
  maritalStatus: '',
  nationalId: '',
  physicalAddress: '',
  city: '',
  province: '',
  phoneNumber: '',
  email: '',
  employmentStatus: '',
  employerName: '',
  employerAddress: '',
  jobTitle: '',
  businessName: '',
  businessType: '',
  businessRegistration: '',
  monthlyIncome: ''
};

const defaultLoanDetails: LoanDetails = {
  loanType: '',
  loanAmount: undefined,
  loanTerm: undefined,
  loanPurpose: '',
  otherPurpose: '',
  businessRegNumber: '',
  yearsInOperation: '',
  assetDescription: '',
  assetValue: ''
};

const defaultApplicationData: ApplicationData = {
  personalInfo: defaultPersonalInfo,
  loanDetails: defaultLoanDetails,
  documents: {}
};

const LoanApplicationContext = createContext<LoanApplicationContextType | undefined>(undefined);

export function LoanApplicationProvider({ children }: { children: ReactNode }) {
  const [currentStep, setCurrentStep] = useState<number>(() => {
    const saved = localStorage.getItem('loanApplicationStep');
    return saved ? parseInt(saved, 10) : 1;
  });

  const [applicationData, setApplicationData] = useState<ApplicationData>(() => {
    const saved = localStorage.getItem('loanApplicationData');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return defaultApplicationData;
      }
    }
    return defaultApplicationData;
  });

  useEffect(() => {
    localStorage.setItem('loanApplicationStep', currentStep.toString());
  }, [currentStep]);

  useEffect(() => {
    const dataToSave = {
      ...applicationData,
      documents: {}
    };
    localStorage.setItem('loanApplicationData', JSON.stringify(dataToSave));
  }, [applicationData]);

  const updatePersonalInfo = (info: Partial<PersonalInfo>) => {
    // Sanitize all text inputs before storing
    const sanitized: Partial<PersonalInfo> = {};
    
    if (info.firstName !== undefined) sanitized.firstName = sanitizeText(info.firstName);
    if (info.lastName !== undefined) sanitized.lastName = sanitizeText(info.lastName);
    if (info.dateOfBirth !== undefined) sanitized.dateOfBirth = info.dateOfBirth;
    if (info.gender !== undefined) sanitized.gender = info.gender;
    if (info.maritalStatus !== undefined) sanitized.maritalStatus = info.maritalStatus;
    if (info.nationalId !== undefined) sanitized.nationalId = sanitizeAlphanumeric(info.nationalId);
    if (info.physicalAddress !== undefined) sanitized.physicalAddress = sanitizeText(info.physicalAddress);
    if (info.city !== undefined) sanitized.city = sanitizeText(info.city);
    if (info.province !== undefined) sanitized.province = info.province;
    if (info.phoneNumber !== undefined) sanitized.phoneNumber = sanitizePhone(info.phoneNumber);
    if (info.email !== undefined) sanitized.email = sanitizeEmail(info.email);
    if (info.employmentStatus !== undefined) sanitized.employmentStatus = info.employmentStatus;
    if (info.employerName !== undefined) sanitized.employerName = sanitizeText(info.employerName);
    if (info.employerAddress !== undefined) sanitized.employerAddress = sanitizeText(info.employerAddress);
    if (info.jobTitle !== undefined) sanitized.jobTitle = sanitizeText(info.jobTitle);
    if (info.businessName !== undefined) sanitized.businessName = sanitizeText(info.businessName);
    if (info.businessType !== undefined) sanitized.businessType = sanitizeText(info.businessType);
    if (info.businessRegistration !== undefined) sanitized.businessRegistration = sanitizeAlphanumeric(info.businessRegistration);
    if (info.monthlyIncome !== undefined) sanitized.monthlyIncome = sanitizeNumber(info.monthlyIncome);
    
    setApplicationData(prev => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, ...sanitized }
    }));
  };

  const updateLoanDetails = (details: Partial<LoanDetails>) => {
    // Sanitize loan details inputs
    const sanitized: Partial<LoanDetails> = {};
    
    if (details.loanType !== undefined) sanitized.loanType = details.loanType;
    if (details.loanAmount !== undefined) sanitized.loanAmount = details.loanAmount;
    if (details.loanTerm !== undefined) sanitized.loanTerm = details.loanTerm;
    if (details.loanPurpose !== undefined) sanitized.loanPurpose = details.loanPurpose;
    if (details.otherPurpose !== undefined) sanitized.otherPurpose = sanitizeText(details.otherPurpose);
    if (details.businessRegNumber !== undefined) sanitized.businessRegNumber = sanitizeAlphanumeric(details.businessRegNumber);
    if (details.yearsInOperation !== undefined) sanitized.yearsInOperation = details.yearsInOperation;
    if (details.assetDescription !== undefined) sanitized.assetDescription = sanitizeText(details.assetDescription);
    if (details.assetValue !== undefined) sanitized.assetValue = sanitizeNumber(details.assetValue);
    
    setApplicationData(prev => ({
      ...prev,
      loanDetails: { ...prev.loanDetails, ...sanitized }
    }));
  };

  const updateDocuments = (documents: Record<string, UploadedFile>) => {
    setApplicationData(prev => ({
      ...prev,
      documents
    }));
  };

  const resetApplication = () => {
    localStorage.removeItem('loanApplicationData');
    localStorage.removeItem('loanApplicationStep');
    setApplicationData(defaultApplicationData);
    setCurrentStep(1);
  };

  return (
    <LoanApplicationContext.Provider
      value={{
        applicationData,
        currentStep,
        updatePersonalInfo,
        updateLoanDetails,
        updateDocuments,
        setCurrentStep,
        resetApplication
      }}
    >
      {children}
    </LoanApplicationContext.Provider>
  );
}

export function useLoanApplication() {
  const context = useContext(LoanApplicationContext);
  if (!context) {
    throw new Error('useLoanApplication must be used within LoanApplicationProvider');
  }
  return context;
}